<script setup lang="ts">
import { cn } from '@/lib/utils'
import { CheckIcon, Cross1Icon } from '@radix-icons/vue'
import { Loader } from 'lucide-vue-next'
import { computed, nextTick, onMounted, ref } from 'vue'
import { Button } from '@/components/ui/button'
import TooltipProvider from '@/components/ui/tooltip/TooltipProvider.vue'
import Tooltip from '@/components/ui/tooltip/Tooltip.vue'
import TooltipTrigger from '@/components/ui/tooltip/TooltipTrigger.vue'
import TooltipContent from '@/components/ui/tooltip/TooltipContent.vue'

const model = defineModel<string | number | null | undefined>()

const props = withDefaults(
    defineProps<{
        placeholder?: string
        type?:
            | 'text'
            | 'number'
            | 'password'
            | 'email'
            | 'url'
            | 'tel'
            | 'search'
            | 'date'
            | 'time'
            | 'file'
        disabled?: boolean
        loading?: boolean
        saving?: boolean
        saved?: boolean
        label?: string
        focus?: boolean
        withSaveButton?: boolean
        lowercase?: boolean
        title?: string
        accept?: string
    }>(),
    {
        type: 'text',
        disabled: false,
        loading: false,
        saving: false,
        saved: false,
        focus: false,
        withSaveButton: false,
        lowercase: false,
        label: undefined,
        placeholder: '',
        title: '',
        accept: '',
    }
)

const emits = defineEmits<{
    (e: 'update:modelValue', payload: string | number | undefined | null): void
    (e: 'save', payload: string | number | undefined | null): void
}>()

const classes =
    'flex items-center w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground has-focus:border-blue-500 disabled:cursor-not-allowed disabled:opacity-50'

const value = computed({
    get() {
        return model.value
    },
    set(value) {
        if (value === '') {
            value = null
        }

        if (props.lowercase && typeof value === 'string') {
            value = value?.toLowerCase()
        }

        emits('update:modelValue', value)
    },
})

const target = ref()
const cachedValue = ref()

const showSaveButton = computed(() => {
    if (cachedValue.value === undefined) {
        return false
    }

    return props.withSaveButton && !props.saved && cachedValue.value !== value.value
})

onMounted(() => {
    nextTick(() => {
        if (props.focus) {
            target.value.focus()
        }
    })

    cachedValue.value = value.value
})

const save = () => {
    cachedValue.value = value.value

    emits('save', value.value)
}

const clear = () => {
    value.value = null
}
</script>

<template>
    <div :class="cn(classes, $attrs.class ?? '')">
        <div class="w-full">
            <div v-if="label" class="flex items-center mb-2 font-semibold">
                <div class="flex-1">{{ label }}</div>
                <Loader v-if="saving || loading" class="h-4 w-4 shrink-0 opacity-50 animate-spin" />
                <CheckIcon
                    v-if="saved && !saving"
                    class="h-4 w-4 shrink-0 opacity-50 text-green-600"
                />
            </div>
            <div class="flex items-center space-x-1 group">
                <input
                    ref="target"
                    v-model="value"
                    :title="title"
                    :type="type"
                    :placeholder="placeholder"
                    :disabled="disabled || loading || saving"
                    :accept="accept"
                    class="flex-1 py-[3px] text-black"
                    style="width: inherit"
                />
                <TooltipProvider v-if="value">
                    <Tooltip>
                        <TooltipTrigger>
                            <Button variant="icon" size="icon-xs" class="hidden group-hover:flex">
                                <Cross1Icon class="w-3 h-3" @click="clear" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>Clear</TooltipContent>
                    </Tooltip>
                </TooltipProvider>
                <Loader
                    v-if="(saving || loading) && label === undefined && !showSaveButton"
                    class="h-4 w-4 shrink-0 opacity-50 animate-spin"
                />
                <CheckIcon
                    v-if="saved && !saving && label === undefined"
                    class="h-4 w-4 shrink-0 opacity-50 text-green-600"
                />
                <Button v-if="showSaveButton" size="xs" :loading="saving" @click="save"
                    >Save</Button
                >
            </div>
        </div>
    </div>
</template>
